import { toast } from '@approvalmax/ui/src/components';
import { browserHelpers, errorHelpers } from '@approvalmax/utils';
import { domain } from 'modules/data';
import { useCallback, useRef } from 'react';
import { gaService } from 'services/ga';
import { routingService } from 'services/routing';
import { storageService } from 'services/storage';
import { signUpOauthProviderStorageKey } from 'shared/constants';
import {
    useRegisterViaGoogle,
    useRegisterViaMicrosoft,
    useRegisterViaQBooks,
    useRegisterViaXero,
} from 'shared/data/webApp/v1';
import { saveQualificationQuestionsAnswers } from 'shared/helpers';
import { getPath, Path } from 'urlBuilder';

import { getExternalSsoAppUrl } from '../helpers';
import { messages } from '../messages';
import { SsoRegisterParams } from '../types';

export const useSsoRegistration = () => {
    const { mutateAsync: registerViaXero, isLoading: isLoadingRegisterViaXero } = useRegisterViaXero();
    const { mutateAsync: registerViaQBooks, isLoading: isLoadingRegisterViaQBooks } = useRegisterViaQBooks();
    const { mutateAsync: registerViaGoogle, isLoading: isLoadingRegisterViaGoogle } = useRegisterViaGoogle();
    const { mutateAsync: registerViaMicrosoft, isLoading: isLoadingRegisterViaMicrosoft } = useRegisterViaMicrosoft();

    const registerParamsRef = useRef<SsoRegisterParams>();

    const getRegisterViaSsoData = useCallback(
        (provider: domain.OAuthProvider) => {
            switch (provider) {
                case domain.OAuthProvider.Xero:
                    return { handler: registerViaXero, method: 'xero_sso' };

                case domain.OAuthProvider.QBooks:
                    return { handler: registerViaQBooks, method: 'qbo_sso' };

                case domain.OAuthProvider.Google:
                    return { handler: registerViaGoogle, method: 'google_sso' };

                case domain.OAuthProvider.Microsoft:
                    return { handler: registerViaMicrosoft, method: 'microsoft_sso' };

                default:
                    throw errorHelpers.assertNever(provider);
            }
        },
        [registerViaGoogle, registerViaMicrosoft, registerViaQBooks, registerViaXero]
    );

    const handleRegisterSuccess = useCallback(
        async (params: SsoRegisterParams) => {
            storageService.setAuthenticated(true);

            const { method } = getRegisterViaSsoData(params.oauthProvider);

            if (params.oauthProvider && browserHelpers.isMobile()) {
                localStorage.setItem(signUpOauthProviderStorageKey, params.oauthProvider);
            }

            await gaService.sendEvent('sign_up', { method });

            registerParamsRef.current = params;
        },
        [getRegisterViaSsoData]
    );

    const handleRedirectAfter = useCallback(() => {
        const params = registerParamsRef.current;

        if (!params) {
            return routingService.reloadToDefaultPath();
        }

        if (params.app) {
            return routingService.navigateToExternalUrl(getExternalSsoAppUrl(params.app));
        }

        if (params.redirectUrl) {
            return routingService.reloadToUrl(params.redirectUrl);
        }

        routingService.reloadToUrl(getPath(Path.signUpWithFinalize, params.oauthProvider));
    }, []);

    const handleRegister = useCallback(
        async (params: SsoRegisterParams) => {
            const { handler: registerViaSso } = getRegisterViaSsoData(params.oauthProvider);

            try {
                await registerViaSso({
                    body: {
                        firstName: params.firstName,
                        lastName: params.lastName,
                        phone: params.phone,
                        email: params.email,
                        timeZoneForEmail: window.ApprovalMax.userTimeZone,
                        transientToken: params.signUpCode,
                        sendConfirmationLink: true,
                        acceptedProductNewsEmails: params.subscribe,
                        qualificationQuestionAnswers: params.qualificationQuestionsAnswers,
                        isMobileFlow: browserHelpers.isMobile(),
                    },
                });

                saveQualificationQuestionsAnswers(params.qualificationQuestionsAnswers);

                await handleRegisterSuccess(params);
            } catch (_error) {
                toast.error(messages.errorSignup);
            }
        },
        [getRegisterViaSsoData, handleRegisterSuccess]
    );

    return {
        handleRegister,
        handleRedirectAfter,
        isLoading:
            isLoadingRegisterViaXero ||
            isLoadingRegisterViaQBooks ||
            isLoadingRegisterViaGoogle ||
            isLoadingRegisterViaMicrosoft,
    };
};
